import { useEffect, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { Avatar } from '../../components/Avatar'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'
import {
  fetchWordArchive, fetchWordArchiveChart,
  type WordArchiveChartRow, type WordArchiveWeek,
} from '../../lib/api/wordGames'

const TITLES: Record<'fake_out' | 'close_enough', string> = {
  fake_out: 'FAKE OUT',
  close_enough: 'CLOSE ENOUGH',
}

const ACCENTS: Record<'fake_out' | 'close_enough', {
  bgTitle: string; text: string; border: string; bg: string
}> = {
  fake_out: {
    bgTitle: '#8FAF52',
    text: 'text-game-fakeout-dark',
    border: 'border-game-fakeout-dark',
    bg: 'bg-game-fakeout-light',
  },
  close_enough: {
    bgTitle: '#C9C930',
    text: 'text-game-closeenough-dark',
    border: 'border-game-closeenough',
    bg: 'bg-game-closeenough-light',
  },
}

interface Props {
  game: 'fake_out' | 'close_enough'
  /** Jump straight to one week's chart (trophy taps land here). */
  initialWeek?: string | null
  onBack: () => void
  /** Chart rows open the player's profile, like every other leaderboard. */
  onOpenProfile?: (profileId: string, week: string) => void
}

function fmtWeek(iso: string): string {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Past weeks for a daily word game, one row per completed week in the
 * "N players · winner" house style, opening into that week's top ten.
 * Owner rule: the top ten, and always your own score. If you placed
 * below ten your row sits under the ten; if you did not play that
 * week it says so. Rows open the player's public profile.
 */
export function WordArchiveScreen({ game, initialWeek = null, onBack, onOpenProfile }: Props) {
  const t = useT()
  const [weeks, setWeeks] = useState<WordArchiveWeek[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(initialWeek)
  const [chart, setChart] = useState<WordArchiveChartRow[] | null>(null)
  const [chartError, setChartError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchWordArchive(game)
      .then(w => { if (!cancelled) setWeeks(w) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [game])

  useEffect(() => {
    if (!selected) { setChart(null); return }
    let cancelled = false
    setChart(null)
    setChartError(null)
    fetchWordArchiveChart(game, selected)
      .then(r => { if (!cancelled) setChart(r) })
      .catch(e => { if (!cancelled) setChartError(readError(e)) })
    return () => { cancelled = true }
  }, [game, selected])

  const title = TITLES[game]
  const accent = ACCENTS[game]
  const week = weeks?.find(w => w.week_start === selected) ?? null
  // The server sends the top ten plus the caller's own row (flagged
  // is_me) even when it ranks below ten, so split it back out here.
  const topTen = chart?.filter(r => r.rank <= 10) ?? []
  const me = chart?.find(r => r.is_me) ?? null
  const meBelowTen = me && me.rank > 10 ? me : null

  const renderRow = (r: WordArchiveChartRow) => {
    const rowClass = `flex items-center gap-3 rounded-2xl px-3 py-2 border w-full text-left
                      ${r.is_me ? `${accent.border} ${accent.bg}` : 'bg-white border-brand-border'}`
    const inner = (
      <>
        <span className={`w-7 text-center font-display font-bold ${r.rank === 1 ? accent.text : 'text-brand-muted'}`}>
          {r.rank}
        </span>
        <Avatar name={r.display_name} avatarUrl={r.avatar_url} size="sm" />
        <span className="flex-1 min-w-0 truncate text-sm font-semibold text-brand-text">
          {r.display_name}
          {r.is_me && (
            <span className={`ml-2 text-[10px] font-semibold uppercase tracking-widest ${accent.text}`}>
              {t('You')}
            </span>
          )}
        </span>
        <span className="text-xs text-brand-muted">
          {r.days_played}d
        </span>
        <span className="font-display font-bold text-brand-text">
          {r.total}
        </span>
      </>
    )
    // Other players' rows open their profile; your own row is not a
    // link (your shelf lives on the Profile tab).
    return onOpenProfile && !r.is_me && selected ? (
      <button key={r.profile_id} type="button"
              onClick={() => onOpenProfile(r.profile_id, selected)}
              className={`${rowClass} active:scale-[0.98] transition-transform`}>
        {inner}
      </button>
    ) : (
      <div key={r.profile_id} className={rowClass}>{inner}</div>
    )
  }

  return (
    <div className="screen">
      <BackgroundTitle text={title} color={accent.bgTitle} />
      <PageHeader
        title={t('{title} ARCHIVE', { title })}
        subtitle={selected ? t('Week of {date}', { date: fmtWeek(selected) }) : t('Every completed week')}
        onBack={selected ? () => setSelected(null) : onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-8 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-3">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {!selected && (
          <>
            {!weeks && !error && (
              <div className="h-32 rounded-2xl bg-brand-elevated animate-pulse mt-1" />
            )}
            {weeks?.length === 0 && (
              <p className="text-sm text-brand-muted text-center py-10">
                {t('No completed weeks yet. The first one closes Sunday night.')}
              </p>
            )}
            <div className="space-y-2.5 mt-1">
              {weeks?.map(w => (
                <button
                  key={w.week_start}
                  onClick={() => setSelected(w.week_start)}
                  className="w-full bg-white border border-brand-border rounded-2xl
                             p-4 text-left flex items-center gap-3
                             active:scale-[0.98] transition-transform"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-brand-text">
                      {t('Week of {date}', { date: fmtWeek(w.week_start) })}
                    </p>
                    <p className="text-xs text-brand-muted mt-0.5 truncate">
                      {w.players === 1 ? t('1 player') : t('{n} players', { n: w.players })}
                      {w.winner_name ? ` · ${w.winner_name}` : ''}
                    </p>
                  </div>
                  <span className={`font-display font-bold shrink-0 ${accent.text}`}>
                    {w.winner_total ?? ''}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {selected && (
          <>
            {chartError && (
              <p className="text-sm text-brand-danger mt-2">{chartError}</p>
            )}
            {!chart && !chartError && (
              <div className="h-64 rounded-2xl bg-brand-elevated animate-pulse mt-1" />
            )}
            {week?.winner_name && (
              <div className={`card p-4 mt-1 rounded-2xl text-center border-2
                              ${accent.border} ${accent.bg}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-widest ${accent.text}`}>
                  {t('Week winner')}
                </p>
                <p className="font-display font-bold text-2xl text-brand-text mt-0.5">
                  {week.winner_name}
                </p>
              </div>
            )}
            <div className="space-y-2 mt-3">
              {topTen.map(renderRow)}
            </div>
            {meBelowTen && (
              <>
                <p className="text-center text-brand-muted tracking-[0.4em] leading-none py-2 select-none" aria-hidden="true">
                  ···
                </p>
                {renderRow(meBelowTen)}
              </>
            )}
            {chart && !me && (
              <p className="text-xs text-brand-muted text-center mt-4">
                {t('You did not play this week')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
